import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, FileText, Users, BarChart2, Clock,
  AlertTriangle, Pill, ClipboardList, TrendingDown,
  UserCheck, RefreshCw, Layers, Target, Activity,
  DollarSign, Download, TrendingUp, ArrowUpRight,
  ChevronRight, Navigation2, Car, MapPin, StickyNote,
  ShieldAlert, CheckSquare, Search, FileSpreadsheet, Loader2, Receipt,
} from 'lucide-react';
import clsx from 'clsx';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { getBranding, renderExcelHeader, brandHex } from '../../utils/exportBranding';

// ── Brand constants (mirrors ReportLayout) ────────────────────────────────────
const APP_NAME    = 'Rostery';
const PRIMARY_HEX = '4F46E5';
const LIGHT_BG    = 'EEF2FF';
const ALT_ROW     = 'F1F5F9';
const BORDER_HEX  = 'E2E8F0';
const TEXT_HEX    = '1E293B';
const MUTED_HEX   = '64748B';

// Tab accent colours per section (ARGB)
const TAB_COLORS = {
  Shifts:  'FF4F46E5',
  Clients: 'FF059669',
  Staff:   'FF1D4ED8',
  Finance: 'FF7C3AED',
};

// Column letter helper
function colLetter(n) {
  let s = '';
  while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s;
}

function cellText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

// ── Add a styled worksheet to a workbook ─────────────────────────────────────
function addSheet(wb, sheetName, section, reportTitle, rows, columns, branding = null) {
  if (!columns?.length) return;
  const ws = wb.addWorksheet(sheetName.replace(/[\\/?*[\]:]/g, '-').slice(0, 31));

  // Tab colour by section
  const tabArgb = TAB_COLORS[section] || 'FF4F46E5';
  ws.properties.tabColor = { argb: tabArgb };

  const lastCol = colLetter(columns.length);

  // Branded letterhead (logo + org name/details/colour). Returns the column-header row.
  const headerRowNum = renderExcelHeader(wb, ws, {
    branding,
    reportTitle: `${section}  ›  ${reportTitle}`,
    metaText: `Generated: ${format(new Date(), 'dd MMM yyyy  h:mm a')}   |   Records: ${rows.length}`,
    lastCol,
  });

  // Column headers (section tab colour for visual grouping across the workbook)
  const hdr = ws.getRow(headerRowNum);
  hdr.height = 24;
  columns.forEach((col, i) => {
    const cell = hdr.getCell(i + 1);
    cell.value = col.label;
    cell.font  = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
    cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: tabArgb } };
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    cell.border = {
      top: { style: 'medium', color: { argb: tabArgb } },
      bottom: { style: 'medium', color: { argb: tabArgb } },
      left:   { style: 'thin',  color: { argb: tabArgb } },
      right:  { style: 'thin',  color: { argb: tabArgb } },
    };
  });

  // Data rows
  rows.forEach((row, ri) => {
    const exRow = ws.getRow(ri + headerRowNum + 1);
    exRow.height = 18;
    const bg = ri % 2 === 0 ? 'FFFFFFFF' : 'FF' + ALT_ROW;
    columns.forEach((col, ci) => {
      const cell = exRow.getCell(ci + 1);
      cell.value = cellText(row[col.key]);
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.font  = { size: 9, color: { argb: 'FF' + TEXT_HEX } };
      cell.alignment = { vertical: 'middle', indent: 1 };
      cell.border = {
        bottom: { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
        right:  { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
        left:   { style: 'hair', color: { argb: 'FF' + BORDER_HEX } },
      };
    });
  });

  // Summary footer
  const sumRow = rows.length + headerRowNum + 1;
  ws.mergeCells(`A${sumRow}:${lastCol}${sumRow}`);
  const sc = ws.getCell(`A${sumRow}`);
  sc.value = `Total: ${rows.length} record${rows.length !== 1 ? 's' : ''}  ·  ${(branding && branding.name) || APP_NAME}`;
  sc.font  = { bold: true, size: 9, color: { argb: 'FF' + brandHex(branding, PRIMARY_HEX) } };
  sc.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + LIGHT_BG } };
  sc.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(sumRow).height = 20;

  // Auto column widths
  columns.forEach((col, i) => {
    const max = Math.min(45, Math.max(
      col.label.length + 2,
      ...rows.slice(0, 200).map(r => cellText(r[col.key]).length)
    ));
    ws.getColumn(i + 1).width = max + 4;
  });
}

// ── Sheet definitions: { key, title, section, columns } ─────────────────────
const SHEET_DEFS = [
  {
    key: 'vacant_shifts', title: 'Vacant Shifts', section: 'Shifts',
    columns: [
      { key: 'shift_date',    label: 'Date' },
      { key: 'start_time',   label: 'Start' },
      { key: 'end_time',     label: 'End' },
      { key: 'duration_hrs', label: 'Hours' },
      { key: 'shift_type',   label: 'Type' },
      { key: 'client',       label: 'Client' },
      { key: 'facility',     label: 'Facility' },
      { key: 'address',      label: 'Address' },
      { key: 'price_book',   label: 'Price Book' },
    ],
  },
  {
    key: 'shift_details', title: 'Shift Details', section: 'Shifts',
    columns: [
      { key: 'shift_date',  label: 'Date' },
      { key: 'staff_name',  label: 'Staff' },
      { key: 'clients',     label: 'Client(s)' },
      { key: 'start_time',  label: 'Start' },
      { key: 'end_time',    label: 'End' },
      { key: 'hours',       label: 'Hours' },
      { key: 'shift_type',  label: 'Type' },
      { key: 'status',      label: 'Status' },
      { key: 'price_book',  label: 'Price Book' },
      { key: 'address',     label: 'Address' },
    ],
  },
  {
    key: 'service_type', title: 'Service Type', section: 'Shifts',
    columns: [
      { key: 'shift_type',     label: 'Service Type' },
      { key: 'shift_count',    label: 'Total Shifts' },
      { key: 'total_hours',    label: 'Total Hours' },
      { key: 'unique_staff',   label: 'Staff Count' },
      { key: 'unique_clients', label: 'Client Count' },
      { key: 'completed',      label: 'Completed' },
      { key: 'cancelled',      label: 'Cancelled' },
    ],
  },
  {
    key: 'recurring_expiry', title: 'Recurring Shift Expiry', section: 'Shifts',
    columns: [
      { key: 'expires',     label: 'Expiry Date' },
      { key: 'staff_name',  label: 'Staff' },
      { key: 'client',      label: 'Client' },
      { key: 'shift_type',  label: 'Type' },
      { key: 'start_time',  label: 'Start' },
      { key: 'end_time',    label: 'End' },
    ],
  },
  {
    key: 'client_list', title: 'Client List', section: 'Clients',
    columns: [
      { key: 'client_name',       label: 'Client Name' },
      { key: 'ndis_number',       label: 'NDIS Number' },
      { key: 'client_type',       label: 'Funding Type' },
      { key: 'address',           label: 'Address' },
      { key: 'suburb',            label: 'Suburb' },
      { key: 'state',             label: 'State' },
      { key: 'joined_date',       label: 'Joined' },
      { key: 'next_review_date',  label: 'Next Review' },
      { key: 'upcoming_shifts',   label: 'Upcoming Shifts' },
    ],
  },
  {
    key: 'client_checklist', title: 'Client Checklist', section: 'Clients',
    columns: [
      { key: 'client_name',         label: 'Client Name' },
      { key: 'ndis_number',         label: 'NDIS Number' },
      { key: 'client_type',         label: 'Funding Type' },
      { key: 'doc_count',           label: 'Documents' },
      { key: 'expired_docs',        label: 'Expired Docs' },
      { key: 'goal_count',          label: 'Goals' },
      { key: 'active_goals',        label: 'Active Goals' },
      { key: 'has_care_plan',       label: 'Care Plan' },
      { key: 'has_service_agreement', label: 'Service Agreement' },
    ],
  },
  {
    key: 'fund_expiry', title: 'Fund Expiry', section: 'Clients',
    columns: [
      { key: 'client_name',     label: 'Client Name' },
      { key: 'ndis_number',     label: 'NDIS Number' },
      { key: 'fund_name',       label: 'Fund' },
      { key: 'plan_start_date', label: 'Plan Start' },
      { key: 'plan_end_date',   label: 'Plan End' },
      { key: 'fund_amount',     label: 'Budget ($)' },
      { key: 'spent_amount',    label: 'Spent ($)' },
      { key: 'remaining',       label: 'Remaining ($)' },
      { key: 'used_pct',        label: 'Used (%)' },
    ],
  },
  {
    key: 'plan_managed', title: 'Plan Managed Funding', section: 'Clients',
    columns: [
      { key: 'client_name',       label: 'Client Name' },
      { key: 'ndis_number',       label: 'NDIS Number' },
      { key: 'fund_name',         label: 'Fund' },
      { key: 'plan_start_date',   label: 'Plan Start' },
      { key: 'plan_end_date',     label: 'Plan End' },
      { key: 'fund_amount',       label: 'Budget ($)' },
      { key: 'spent_amount',      label: 'Spent ($)' },
      { key: 'remaining',         label: 'Remaining ($)' },
      { key: 'utilisation_pct',   label: 'Utilisation (%)' },
    ],
  },
  {
    key: 'activity_report', title: 'Activity Report', section: 'Staff',
    columns: [
      { key: 'staff_name',   label: 'Staff Name' },
      { key: 'email',        label: 'Email' },
      { key: 'shift_count',  label: 'Shifts' },
      { key: 'total_hours',  label: 'Hours' },
      { key: 'total_km',     label: 'KM' },
      { key: 'completed',    label: 'Completed' },
      { key: 'cancelled',    label: 'Cancelled' },
    ],
  },
  {
    key: 'staff_doc_expiry', title: 'Staff Document Expiry', section: 'Staff',
    columns: [
      { key: 'staff_name',     label: 'Staff Name' },
      { key: 'document_name',  label: 'Document' },
      { key: 'document_type',  label: 'Type' },
      { key: 'expiry_date',    label: 'Expiry Date' },
      { key: 'status',         label: 'Status' },
      { key: 'days_remaining', label: 'Days Remaining' },
    ],
  },
  {
    key: 'staff_compliance', title: 'Staff Compliance', section: 'Staff',
    columns: [
      { key: 'staff_name',         label: 'Staff Name' },
      { key: 'qualification_name', label: 'Qualification' },
      { key: 'expiry_date',        label: 'Expiry Date' },
      { key: 'status',             label: 'Status' },
      { key: 'days_remaining',     label: 'Days Remaining' },
    ],
  },
  {
    key: 'certifications', title: 'Certifications', section: 'Staff',
    columns: [
      { key: 'staff_name',   label: 'Staff Name' },
      { key: 'certification', label: 'Certification' },
      { key: 'issued_date',  label: 'Issued' },
      { key: 'expiry_date',  label: 'Expiry' },
      { key: 'status',       label: 'Status' },
    ],
  },
  {
    key: 'timesheets', title: 'Timesheet Report', section: 'Finance',
    columns: [
      { key: 'shift_date',        label: 'Date' },
      { key: 'staff_name',        label: 'Staff' },
      { key: 'clients',           label: 'Client(s)' },
      { key: 'start_time',        label: 'Rostered Start' },
      { key: 'end_time',          label: 'Rostered End' },
      { key: 'clock_in_time',     label: 'Clock In' },
      { key: 'clock_out_time',    label: 'Clock Out' },
      { key: 'rostered_hrs',      label: 'Rostered Hrs' },
      { key: 'timesheet_status',  label: 'Status' },
    ],
  },
  {
    key: 'invoices', title: 'Invoices', section: 'Finance',
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'invoice_date',   label: 'Date' },
      { key: 'due_date',       label: 'Due Date' },
      { key: 'client_name',    label: 'Client' },
      { key: 'ndis_number',    label: 'NDIS Number' },
      { key: 'fund_name',      label: 'Fund' },
      { key: 'total_amount',   label: 'Amount ($)' },
      { key: 'status',         label: 'Status' },
    ],
  },
  {
    key: 'fund_balances', title: 'Fund Balances', section: 'Finance',
    columns: [
      { key: 'client_name',   label: 'Client' },
      { key: 'ndis_number',   label: 'NDIS Number' },
      { key: 'fund_name',     label: 'Fund' },
      { key: 'fund_amount',   label: 'Budget ($)' },
      { key: 'spent_amount',  label: 'Spent ($)' },
      { key: 'remaining',     label: 'Remaining ($)' },
      { key: 'used_pct',      label: 'Used (%)' },
      { key: 'plan_end_date', label: 'Expiry' },
    ],
  },
];

// ── Main export-all handler ───────────────────────────────────────────────────
async function runExportAll(setExporting) {
  setExporting(true);
  const tid = toast.loading('Fetching all report data…');
  try {
    const branding = await getBranding().catch(() => null);
    const orgName = (branding && branding.name) || 'Rostery';
    const { data } = await api.get('/reports/export-all');
    const sheets = data.sheets || {};

    toast.loading('Building Excel workbook…', { id: tid });
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator   = APP_NAME;
    wb.created   = new Date();

    // ── Cover sheet ──────────────────────────────────────────────────────────
    const cover = wb.addWorksheet('Overview');
    cover.properties.tabColor = { argb: 'FF1E293B' };
    cover.getColumn(1).width = 40;
    cover.getColumn(2).width = 20;

    // Branded letterhead on the cover (logo + org name/details/brand colour).
    const coverHeaderRow = renderExcelHeader(wb, cover, {
      branding,
      reportTitle: 'Full Organisation Report Package',
      metaText: `Generated: ${format(new Date(), 'dd MMMM yyyy  h:mm a')}   |   Date range: ${data.date_range?.start || '—'} to ${data.date_range?.end || '—'}`,
      lastCol: 'B',
    });

    // Section headers + sheet list
    const sections = ['Shifts', 'Clients', 'Staff', 'Finance'];
    let coverRow = coverHeaderRow;
    sections.forEach(sec => {
      const sectionSheets = SHEET_DEFS.filter(s => s.section === sec);
      const hdrRow = cover.getRow(coverRow);
      hdrRow.height = 22;
      cover.mergeCells(`A${coverRow}:B${coverRow}`);
      const hdrCell = cover.getCell(`A${coverRow}`);
      hdrCell.value = sec;
      hdrCell.font  = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      hdrCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: TAB_COLORS[sec] } };
      hdrCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      coverRow++;

      sectionSheets.forEach(def => {
        const rowData = sheets[def.key] || [];
        cover.getRow(coverRow).height = 18;
        cover.getCell(`A${coverRow}`).value = def.title;
        cover.getCell(`A${coverRow}`).font  = { size: 10, color: { argb: 'FF' + TEXT_HEX } };
        cover.getCell(`A${coverRow}`).alignment = { vertical: 'middle', indent: 2 };
        cover.getCell(`A${coverRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        cover.getCell(`A${coverRow}`).border = { bottom: { style: 'hair', color: { argb: 'FF' + BORDER_HEX } } };
        cover.getCell(`B${coverRow}`).value = `${rowData.length} record${rowData.length !== 1 ? 's' : ''}`;
        cover.getCell(`B${coverRow}`).font  = { size: 10, color: { argb: 'FF' + MUTED_HEX } };
        cover.getCell(`B${coverRow}`).alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
        cover.getCell(`B${coverRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        cover.getCell(`B${coverRow}`).border = { bottom: { style: 'hair', color: { argb: 'FF' + BORDER_HEX } } };
        coverRow++;
      });
      coverRow++; // spacing between sections
    });

    // ── Data sheets ──────────────────────────────────────────────────────────
    SHEET_DEFS.forEach(def => {
      const rows = sheets[def.key] || [];
      addSheet(wb, def.title, def.section, def.title, rows, def.columns, branding);
    });

    // ── Download ─────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileOrg = orgName.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'Rostery';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileOrg}-All-Reports-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);

    toast.success('All reports exported successfully!', { id: tid });
  } catch (err) {
    console.error(err);
    toast.error('Export failed — please try again', { id: tid });
  } finally {
    setExporting(false);
  }
}

const container = { hidden: {}, show: { transition: { staggerChildren: .05 } } };
const item      = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: .25 } } };

/* Icon color themes per section */
const THEMES = {
  Shifts:  { from: '#6366f1', to: '#4f46e5' },
  Clients: { from: '#10b981', to: '#059669' },
  Staff:   { from: '#3b82f6', to: '#1d4ed8' },
  Finance: { from: '#8b5cf6', to: '#7c3aed' },
};

function ReportCard({ icon: Icon, title, description, href, comingSoon, theme }) {
  const navigate = useNavigate();
  const grad = theme || THEMES.Shifts;

  return (
    <motion.div
      variants={item}
      onClick={() => !comingSoon && navigate(href)}
      whileHover={!comingSoon ? { y: -3, transition: { duration: .18 } } : {}}
      className={clsx(
        'relative bg-white rounded-2xl border p-5 transition-all duration-200',
        comingSoon
          ? 'opacity-60 cursor-default border-slate-100'
          : 'cursor-pointer group border-slate-100 hover:border-primary-200',
      )}
      style={!comingSoon ? { boxShadow: '0 1px 3px rgba(0,0,0,.05)' } : {}}
      onMouseEnter={e => {
        if (!comingSoon) e.currentTarget.style.boxShadow = '0 8px 24px -4px rgba(0,0,0,.1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.05)';
      }}
    >
      {comingSoon && (
        <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 uppercase tracking-wide">
          Soon
        </span>
      )}

      <div className="flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
          style={{ background: `linear-gradient(135deg, ${grad.from}, ${grad.to})`, boxShadow: `0 4px 10px ${grad.from}40` }}>
          <Icon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-slate-900 font-semibold text-sm mb-1 group-hover:text-primary-700 transition-colors">{title}</h3>
          <p className="text-slate-400 text-xs leading-relaxed">{description}</p>
          {!comingSoon && (
            <div className="mt-2.5 flex items-center gap-1 text-primary-600 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
              View Report <ArrowUpRight size={11} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Section({ title, theme, children }) {
  const grad = THEMES[title] || THEMES.Shifts;
  return (
    <motion.div variants={item}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1 h-5 rounded-full" style={{ background: `linear-gradient(to bottom, ${grad.from}, ${grad.to})` }} />
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
      </div>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
      >
        {React.Children.map(children, child =>
          React.cloneElement(child, { theme: grad })
        )}
      </motion.div>
    </motion.div>
  );
}

export default function ReportHub() {
  const [exporting, setExporting] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">Report Hub</h1>
          <p className="text-sm text-slate-500 mt-0.5">Analytics & compliance reporting across your organisation</p>
        </div>
        <button
          onClick={() => runExportAll(setExporting)}
          disabled={exporting}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200',
            exporting
              ? 'bg-emerald-100 text-emerald-500 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md active:scale-95',
          )}
        >
          {exporting
            ? <><Loader2 size={15} className="animate-spin" /> Building…</>
            : <><FileSpreadsheet size={15} /> Export All Reports</>}
        </button>
      </div>

      <Section title="Shifts">
        <ReportCard icon={Calendar}      href="/reports/vacant-shifts"               title="Vacant Shifts"                        description="Shifts that don't have assigned staff members." />
        <ReportCard icon={RefreshCw}     href="/reports/recurring-shift-expiry"      title="Recurring Shift Expiry"               description="Recurring shift programs expiring soon." />
        <ReportCard icon={ClipboardList} href="/reports/shift-details"               title="Shift Details"                        description="All shifts performed or scheduled with full details." />
        <ReportCard icon={FileText}      href="/reports/declined-shifts"             title="Declined Shifts by Staff"             description="Shifts that were allocated and later declined by staff." />
        <ReportCard icon={Calendar}      href="/reports/care-worker-schedule"        title="Care Worker Service Schedule"         description="Shifts scheduled per care worker with client and location details." />
        <ReportCard icon={Calendar}      href="/reports/care-worker-schedule-km-exp" title="Care Worker Schedule (KM, EXP)"       description="Care worker schedule including kilometres driven and expenses." />
        <ReportCard icon={BarChart2}     href="/reports/service-type"                title="Service Type Report"                 description="Shift counts, hours, staff and client breakdown grouped by service type." />
      </Section>

      <Section title="Clients">
        <ReportCard icon={FileText}       href="/reports/client-document-expiry"  title="Client Document Expiry"        description="Client documents that are expiring or have expired." />
        <ReportCard icon={Activity}       href="/reports/care-plan-progress"      title="Care Plan Progress"            description="Monitor client activity on their care plans." />
        <ReportCard icon={Layers}         href="/reports/care-plan-activity"      title="Care Plan & Goal Activity"     description="Care Plan tasks, goals, responses and progressions." />
        <ReportCard icon={Pill}           href="/reports/medication-adherence"    title="Medication Adherence"          description="Track client medication adherence and identify missed doses." />
        <ReportCard icon={Pill}           href="/reports/medication-usage"        title="Medication Usage"              description="All clients using specific medications with plan details." />
        <ReportCard icon={DollarSign}     href="/reports/fund-expiry"             title="Fund Expiry"                   description="Client funding packages expiring or already expired." />
        <ReportCard icon={TrendingDown}   href="/reports/fund-depletion"          title="Fund Depletion Forecast"       description="Predicts when client funding will run out." />
        <ReportCard icon={UserCheck}      href="/reports/client-onboarding"       title="Client Onboarding Status"      description="Track new clients through the onboarding process." />
        <ReportCard icon={Download}       href="/reports/client-full-report"          title="Client Full Report"              description="Download all client documents for compliance support." />
        <ReportCard icon={CheckSquare}    href="/reports/client-checklist"            title="Client Checklist"                description="Active clients checklist — docs, goals, care plan, service agreement status." />
        <ReportCard icon={Users}          href="/reports/client-profile"              title="Client Profile Report"           description="Full client profile snapshot — demographics, NDIS plan, budget and goals." />
        <ReportCard icon={StickyNote}     href="/reports/client-shift-notes"          title="Client Shift Notes"              description="The shift notes held on each participant, dated to the day of service. Filter by participant, staff and date, then export." />
        <ReportCard icon={DollarSign}     href="/reports/plan-managed-funding"        title="Plan Managed Funding"            description="Plan managed funding balances, utilisation percentages and shift activity per period." />
      </Section>

      <Section title="Staff">
        <ReportCard icon={TrendingUp}    href="/reports/activity"                    title="Activity Report"                    description="Staff and client shift activity — hours, mileage, shift counts." />
        <ReportCard icon={Navigation2}   href="/mileage"                             title="Mileage Tracker"                    description="All trips logged by support workers — distance, from/to, KM totals." />
        <ReportCard icon={FileText}      href="/reports/staff-document-expiry"       title="Staff Document Expiry"              description="Staff documents that are expiring or have expired." />
        <ReportCard icon={Clock}         href="/reports/clock-variance"              title="Clock In/Out Variance"              description="Staff clock-in/out variance from scheduled times." />
        <ReportCard icon={Clock}         href="/reports/clock-in-out"                title="Clock In and Out Report"            description="Full listing of all clock-in and clock-out events with distance and verification status." />
        <ReportCard icon={MapPin}        href="/reports/radius"                      title="Radius Report"                     description="Clock-in/out events outside the configured geofence radius." />
        <ReportCard icon={Car}           href="/reports/car-rego"                    title="Car Rego Report"                   description="Vehicle registration details and expiry status for all staff members." />
        <ReportCard icon={Car}           href="/reports/fleet-usage"                 title="Fleet Usage Report"                description="Company vehicle usage — shifts assigned per vehicle with km, staff and client details." />
        <ReportCard icon={BarChart2}     href="/reports/work-hours-compliance"       title="Work Hours Compliance"              description="Monitor individual staff compliance with work hour limits." />
        <ReportCard icon={UserCheck}     href="/reports/staff-compliance"            title="Employee Compliance Check"          description="Document validity and compliance status across your team." />
        <ReportCard icon={CheckSquare}   href="/reports/compliance-extended"         title="Employee Compliance Extended"       description="Compliance check with counts — expired, expiring, valid quals and documents per staff." />
        <ReportCard icon={UserCheck}     href="/reports/certifications"              title="Certifications"                    description="Qualification expiry tracker — expired, expiring soon, and valid certifications per staff member." />
        <ReportCard icon={StickyNote}    href="/reports/employee-notes"              title="Employee Notes — All Employees"    description="Every note attached to a staff member — the shift notes they wrote, and the HR file kept about them. Filter by employee, participant and date." />
        <ReportCard icon={StickyNote}    href="/reports/staff-shift-notes"           title="Staff Shift Notes"                 description="Every progress note written by each support worker, dated to the shift it covers. Filter by staff, participant and date, then export." />
        <ReportCard icon={ShieldAlert}   href="/reports/staff-warning-report"        title="Staff Warning"                     description="All staff warnings issued across the organisation." />
        <ReportCard icon={CheckSquare}   href="/reports/staff-warning-acknowledgement" title="Staff Warning Acknowledgement"  description="Warnings that have been acknowledged by the staff member." />
        <ReportCard icon={UserCheck}     href="/reports/staff-onboarding"            title="Staff Onboarding Status"           description="Track new staff members through the onboarding process." />
        <ReportCard icon={DollarSign}    href="/reports/pay-review"                  title="Pay Review Date"                   description="Staff members due for pay rate reviews." />
        <ReportCard icon={Users}         href="/reports/human-capital"               title="Human Capital Variance"            description="Compares minimum work hours of staff with actual hours." />
      </Section>

      <Section title="Finance">
        <ReportCard icon={TrendingUp}    href="/reports/billing"                          title="Billing Report"                        description="Revenue, billing data and financial summaries by period." />
        <ReportCard icon={Receipt}       href="/reports/reimbursements"                   title="Reimbursements"                        description="Staff expense claims — totals by status, category and worker." />
        <ReportCard icon={DollarSign}    href="/reports/fund-balances"                    title="Fund Balances"                         description="Current NDIS funding balances across all active clients." />
        <ReportCard icon={Activity}      href="/reports/performance"                      title="Performance Overview"                  description="KPI performance metrics and team productivity." />
        <ReportCard icon={AlertTriangle} href="/reports/exception/delivered-vs-invoiced"  title="Delivered vs Invoiced"                 description="Identify gaps between services delivered and invoiced." />
        <ReportCard icon={DollarSign}    href="/reports/payslip-report"                   title="Payslip Summary"                       description="Payroll run summary — gross wages, allowances and totals per staff member." />
        <ReportCard icon={Search}        href="/reports/timesheet-query"                  title="Timesheet Query Report"                description="Query and filter timesheets by staff, client, shift type and status." />
        <ReportCard icon={ClipboardList} href="/reports/timesheet-report"                 title="Timesheet Report"                      description="Full timesheet listing with clock-in/out and rostered hours." />
        <ReportCard icon={CheckSquare}   href="/reports/timesheet-approved"               title="Timesheet Report (Approved)"           description="Approved timesheets only — total approved hours per period." />
        <ReportCard icon={CheckSquare}   href="/reports/timesheet-approved-rostered"      title="Timesheet Report (Approved + Rostered)" description="Approved timesheets compared against original rostered time with variance." />
        <ReportCard icon={DollarSign}    href="/reports/timesheet-pay-rates"              title="Timesheet Report With Pay Rates"       description="Timesheets with pay band, multiplier, pay amount, KM and expenses." />
      </Section>
    </motion.div>
  );
}
